// Single source of truth for every constants-driven attribute popover.
// Each entry mirrors the prototype in `src/Mapping UI/data/screens/*` while
// binding label strings to the numeric IDs stored in `src/json/constants.js`.
// Backend numeric IDs are the contract — never change them; rename labels in
// place if the spec asks for a relabel.
//
// Field shape:
//   slot:                  'description' | 'type' | 'size'
//   label:                 stored label (default display text)
//   displayLabel?:         static rename (e.g. SELECT TYPE -> SELECT BANK TYPE)
//   displayLabelByValue?:  rename keyed by parent value's LABEL or composite
//                          'PARENT1 | PARENT2'
//   options:               [{label, value}] with numeric IDs
//   optionRows?:           label arrays — drives visual row grouping
//   dependsOn?:            parent slot name, or array of parent slot names
//   optionsByValue?:       label-array map keyed by parent value's LABEL or
//                          composite key
//   fallback?:             label array used when no key matches
//   required?:             true blocks Save when the field is visible & empty
//
// When adding a new element: see CLAUDE.md, "Adding New Element Attributes".

import {
  backroundDesc,
  backroundType,
  BridgePolygonDescription,
  BunkerPolygonDescription,
  BunkerPolygonType,
  buildingDesc,
  buildingSize,
  buildingType,
  cartPathDesc,
  clubhouseDesc,
  clubhouseSize,
  clubhouseType,
  creekSize,
  faciltyFixtureType,
  faciltyForestType,
  faciltyHazardAreaSize,
  faciltyHazardCliffDesc,
  faciltyHazardCliffType,
  faciltyHazardNativeAreaDesc,
  faciltyHazardNativeAreaType,
  faciltyHazardSize,
  faciltyHazardTreeDescription,
  faciltyHazardType,
  fenceDesc,
  fenceSize,
  lavaType,
  parkingLotDesc,
  residenceDesc,
  residenceSize,
  residenceType,
  streetDesc,
  streetSize,
  waterDesc,
  waterSize,
  waterType,
} from "json/constants";

// Resolve an array of labels to {label, value} objects from a constants table.
// Throws at module load if a label is missing — surfaces typos and missing
// rename migrations immediately rather than at first popover open.
const opts = (table, labels) =>
  labels.map((label) => {
    const found = table.find((o) => o.label === label);
    if (!found) {
      throw new Error(
        `attributeFieldDefs: label "${label}" not found in constants table`,
      );
    }
    return { label: found.label, value: found.value };
  });

// Building (Condo / Hotel / Business) share the same option set + Redux slot,
// so the field defs share a single block too.
const buildingFields = [
  {
    slot: "description",
    label: "SELECT DESCRIPTION (COLOR)",
    displayLabel: "SELECT COLOR",
    required: false,
    options: opts(buildingDesc, [
      "WHITE",
      "LIGHT BLUE",
      "DARK BLUE",
      "GRAY",
      "TAN",
      "LIGHT YELLOW",
      "GREEN",
      "CREAM",
      "BROWN",
      "BRICK",
      "GLASS",
    ]),
  },
  {
    slot: "type",
    label: "SELECT TYPE (ROOF)",
    displayLabel: "SELECT ROOF",
    required: false,
    options: opts(buildingType, [
      "BLACK ROOF",
      "LIGHT GRAY ROOF",
      "DARK GRAY",
      "TERRA COTA",
      "RED ROOF",
      "BROWN",
    ]),
  },
  {
    slot: "size",
    label: "SELECT SIZE",
    required: false,
    options: opts(buildingSize, ["SINGLE STORY", "MULTI STORY", "HIGH RISE"]),
  },
];

// PracticeBunker / Sand / Bunker share Description + Type with identical
// options and slot bindings.
const bunkerFamilyFields = [
  {
    slot: "description",
    label: "SELECT DESCRIPTION",
    required: false,
    options: opts(BunkerPolygonDescription, ["CLEAN EDGE", "ROUGH EDGE"]),
  },
  {
    slot: "type",
    label: "SELECT TYPE",
    displayLabel: "SELECT COLOR",
    required: false,
    options: opts(BunkerPolygonType, ["WHITE", "TAN", "RED", "BLACK"]),
  },
];

export const attributeFieldDefs = {
  Background: {
    title: "Background",
    fields: [
      {
        slot: "description",
        label: "SELECT DESCRIPTION",
        required: false,
        options: opts(backroundDesc, ["ROUGH", "DESERT"]),
      },
      {
        slot: "type",
        label: "SELECT TYPE",
        required: false,
        options: opts(backroundType, [
          "DRY FOREST",
          "WET FOREST",
          "TROPICAL FOREST",
          "FOLIAGE DESERT",
          "FOLIAGE DIRT",
          "DRY DESERT",
          "PLAINS",
          "FARMLAND",
          "RESIDENTIAL",
        ]),
        optionRows: [
          ["DRY FOREST", "WET FOREST", "TROPICAL FOREST"],
          ["FOLIAGE DESERT", "FOLIAGE DIRT", "DRY DESERT"],
          ["PLAINS", "FARMLAND", "RESIDENTIAL"],
        ],
      },
    ],
  },

  CartPath: {
    title: "CartPath",
    fields: [
      {
        slot: "description",
        label: "SELECT DESCRIPTION",
        displayLabel: "SELECT SURFACE",
        required: false,
        options: opts(cartPathDesc, [
          "ASPHALT",
          "CONCRETE",
          "DIRT",
          "SAND",
          "GRAVEL",
        ]),
        optionRows: [
          ["ASPHALT", "CONCRETE"],
          ["DIRT", "SAND", "GRAVEL"],
        ],
      },
    ],
  },

  Residence: {
    title: "Residence",
    fields: [
      {
        slot: "description",
        label: "SELECT DESCRIPTION (COLOR)",
        displayLabel: "SELECT COLOR",
        required: false,
        options: opts(residenceDesc, [
          "CREAM",
          "LIGHT BLUE",
          "DARK BLUE",
          "GRAY",
          "TAN",
          "LIGHT YELLOW",
          "GREEN",
          "WHITE",
          "BROWN",
          "BRICK",
        ]),
      },
      {
        slot: "type",
        label: "SELECT TYPE (ROOF)",
        displayLabel: "SELECT ROOF",
        required: false,
        options: opts(residenceType, [
          "BLACK ROOF",
          "LIGHT GRAY ROOF",
          "DARK GRAY",
          "TERRA COTA",
          "RED ROOF",
          "BROWN",
        ]),
      },
      {
        slot: "size",
        label: "SELECT SIZE",
        required: false,
        options: opts(residenceSize, ["SINGLE STORY", "MULTI STORY"]),
      },
    ],
  },

  Condo: { title: "Condo", fields: buildingFields },
  Hotel: { title: "Hotel", fields: buildingFields },
  Business: { title: "Business", fields: buildingFields },

  ParkingLot: {
    title: "ParkingLot",
    fields: [
      {
        slot: "description",
        label: "SELECT DESCRIPTION",
        displayLabel: "SELECT SURFACE",
        required: true,
        options: opts(parkingLotDesc, ["ASPHALT", "CONCRETE", "GRAVEL"]),
      },
    ],
  },

  Street: {
    title: "Street",
    fields: [
      {
        slot: "description",
        label: "SELECT DESCRIPTION",
        displayLabel: "SELECT SURFACE",
        required: false,
        options: opts(streetDesc, ["ASPHALT", "CONCRETE", "GRAVEL", "DIRT"]),
      },
      {
        slot: "size",
        label: "SELECT SIZE",
        required: false,
        options: opts(streetSize, ["SINGLE LANE", "TWO LANE", "FOUR LANE"]),
      },
    ],
  },

  Fence: {
    title: "Fence",
    fields: [
      {
        slot: "description",
        label: "SELECT DESCRIPTION",
        required: false,
        options: opts(fenceDesc, [
          "HEDGE",
          "RAIL TIES",
          "BRICK",
          "STONE",
          "WOOD",
          "CHAIN LINK",
          "NET",
          "METAL",
          "PICKET",
        ]),
        optionRows: [
          ["HEDGE", "WOOD", "STONE", "BRICK"],
          ["PICKET", "RAIL TIES", "METAL"],
          ["CHAIN LINK", "NET"],
        ],
      },
      {
        slot: "size",
        label: "SELECT SIZE",
        displayLabel: "SELECT HEIGHT",
        required: false,
        options: opts(fenceSize, ["SHORT", "MEDIUM", "TALL"]),
      },
    ],
  },

  PracticeBunker: { title: "PracticeBunker", fields: bunkerFamilyFields },

  Tree: {
    title: "Tree",
    fields: [
      {
        slot: "description",
        label: "SELECT DESCRIPTION",
        required: true,
        options: opts(faciltyHazardTreeDescription, [
          "DECIDUOUS",
          "CONIFER",
          "PINE",
          "WILLOW",
          "CYPRESS",
          "PALM",
          "SHRUB",
          "DESERT SHRUB",
          "CACTUS",
        ]),
        optionRows: [
          ["DECIDUOUS", "CONIFER", "PINE"],
          ["WILLOW", "CYPRESS", "PALM"],
          ["SHRUB", "DESERT SHRUB", "CACTUS"],
        ],
      },
      {
        slot: "type",
        label: "SELECT TYPE",
        displayLabel: "SELECT WIDTH",
        required: true,
        options: opts(faciltyHazardType, ["THIN", "WIDE"]),
      },
      {
        slot: "size",
        label: "SELECT SIZE",
        required: true,
        options: opts(faciltyHazardSize, ["SMALL", "MEDIUM", "LARGE"]),
      },
    ],
  },

  Water: {
    title: "Water",
    fields: [
      {
        slot: "description",
        label: "SELECT DESCRIPTION",
        required: false,
        options: opts(waterDesc, ["CREEK", "POND", "LAKE"]),
      },
      {
        slot: "type",
        label: "SELECT TYPE",
        displayLabel: "SELECT BANK TYPE",
        required: false,
        options: opts(waterType, [
          "GRASS",
          "REEDS",
          "GRASS SWAMP",
          "SAND",
          "ROCKS",
          "CONCRETE WALL",
          "METAL WALL",
          "WOOD WALL",
          "STONE WALL",
        ]),
        optionRows: [
          ["GRASS", "REEDS", "GRASS SWAMP"],
          ["SAND", "ROCKS", "CONCRETE WALL", "METAL WALL", "WOOD WALL", "STONE WALL"],
        ],
      },
      {
        slot: "size",
        label: "SELECT SIZE",
        required: false,
        dependsOn: "type",
        displayLabelByValue: {
          REEDS: "SELECT HEIGHT",
          "GRASS SWAMP": "SELECT HEIGHT",
        },
        options: opts(waterSize, ["SHORT", "MEDIUM", "TALL", "SMALL", "LARGE"]),
        optionsByValue: {
          ROCKS: ["SMALL", "MEDIUM", "LARGE"],
          REEDS: ["SHORT", "MEDIUM", "TALL"],
          "GRASS SWAMP": ["SHORT", "MEDIUM", "TALL"],
          GRASS: [],
          SAND: [],
          "CONCRETE WALL": [],
          "METAL WALL": [],
          "WOOD WALL": [],
          "STONE WALL": [],
        },
        fallback: [],
      },
    ],
  },

  Vegetation: {
    title: "Vegetation",
    fields: [
      {
        slot: "type",
        label: "SELECT TYPE",
        displayLabel: "SELECT WIDTH",
        required: false,
        options: opts(faciltyHazardType, ["THIN", "WIDE"]),
      },
      {
        slot: "size",
        label: "SELECT SIZE",
        required: false,
        options: opts(faciltyHazardSize, ["SMALL", "MEDIUM", "LARGE"]),
      },
    ],
  },

  Bridge: {
    title: "Bridge",
    fields: [
      {
        slot: "description",
        label: "SELECT DESCRIPTION",
        displayLabel: "SELECT MATERIAL",
        required: false,
        options: opts(BridgePolygonDescription, ["WOOD", "STONE", "CONCRETE"]),
      },
    ],
  },

  Sand: { title: "Sand", fields: bunkerFamilyFields },

  Rock: {
    title: "Rock",
    fields: [
      {
        slot: "size",
        label: "SELECT SIZE",
        required: false,
        options: opts(faciltyHazardSize, ["SMALL", "MEDIUM", "LARGE"]),
      },
    ],
  },

  Lava: {
    title: "Lava",
    fields: [
      {
        slot: "type",
        label: "SELECT TYPE",
        displayLabel: "SELECT COLOR",
        required: true,
        options: opts(lavaType, ["BLACK", "RED", "BROWN"]),
      },
    ],
  },

  Cliff: {
    title: "Cliff",
    fields: [
      {
        slot: "description",
        label: "SELECT DESCRIPTION",
        required: false,
        options: opts(faciltyHazardCliffDesc, ["DIRT", "ROCK", "GRASS", "SAND"]),
      },
      {
        slot: "type",
        label: "SELECT TYPE",
        displayLabel: "SELECT COVERAGE",
        required: false,
        options: opts(faciltyHazardCliffType, ["NO FOLIAGE", "FOLIAGE"]),
      },
    ],
  },

  NativeArea: {
    title: "NativeArea",
    fields: [
      {
        slot: "description",
        label: "SELECT DESCRIPTION",
        required: false,
        options: opts(faciltyHazardNativeAreaDesc, [
          "SHORT GRASS",
          "DESERT",
          "LANDSCAPE SOIL",
          "WETLAND",
          "REEDS",
          "SWAMPGRASS",
          "MEADOW GRASS",
          "LINKS GRASS",
          "BUNKER GRASS",
          "ROCKS",
          "CACTUS",
          "WOOD CHIPS",
          "PINE STRAW",
        ]),
        optionRows: [
          ["SHORT GRASS", "DESERT", "LANDSCAPE SOIL"],
          ["WETLAND", "REEDS", "SWAMPGRASS"],
          ["MEADOW GRASS", "LINKS GRASS", "BUNKER GRASS"],
          ["ROCKS", "CACTUS", "WOOD CHIPS", "PINE STRAW"],
        ],
      },
      {
        slot: "type",
        label: "SELECT TYPE",
        required: false,
        dependsOn: "description",
        displayLabelByValue: {
          "WOOD CHIPS": "SELECT COLOR",
          "PINE STRAW": "SELECT COLOR",
          "BUNKER GRASS": "SELECT COLOR",
          "MEADOW GRASS": "SELECT COLOR",
          "LINKS GRASS": "SELECT COLOR",
          REEDS: "SELECT GROUND TYPE",
          SWAMPGRASS: "SELECT GROUND TYPE",
          DESERT: "SELECT FOLIAGE COVERAGE",
          "LANDSCAPE SOIL": "SELECT FOLIAGE COVERAGE",
          ROCKS: "SELECT DENSITY",
          CACTUS: "SELECT DENSITY",
        },
        options: opts(faciltyHazardNativeAreaType, [
          "DRY",
          "WET",
          "NO FOLIAGE",
          "LEAF FOLIAGE",
          "SHRUB FOLIAGE",
          "LEAF AND SHRUB FOLIAGE",
          "BROWN",
          "RED",
          "BLACK",
          "YELLOW",
          "GREEN",
          "LOW DENSITY",
          "MEDIUM DENSITY",
          "HIGH DENSITY",
        ]),
        optionsByValue: {
          ROCKS: ["LOW DENSITY", "MEDIUM DENSITY", "HIGH DENSITY"],
          WETLAND: [],
          "WOOD CHIPS": ["BROWN", "RED", "BLACK"],
          "SHORT GRASS": [],
          DESERT: ["NO FOLIAGE", "LEAF FOLIAGE", "SHRUB FOLIAGE", "LEAF AND SHRUB FOLIAGE"],
          "LANDSCAPE SOIL": ["NO FOLIAGE", "LEAF FOLIAGE", "SHRUB FOLIAGE", "LEAF AND SHRUB FOLIAGE"],
          "BUNKER GRASS": ["GREEN", "BROWN"],
          REEDS: ["WET", "DRY"],
          SWAMPGRASS: ["WET", "DRY"],
          "MEADOW GRASS": ["GREEN", "BROWN"],
          "LINKS GRASS": ["GREEN", "BROWN"],
          "PINE STRAW": ["BROWN", "RED", "YELLOW"],
          CACTUS: ["LOW DENSITY", "MEDIUM DENSITY", "HIGH DENSITY"],
        },
        fallback: [],
      },
      {
        slot: "size",
        label: "SELECT SIZE",
        required: false,
        dependsOn: ["description", "type"],
        // Default to HEIGHT (foliage/vegetation). ROCKS is the only true area-size case.
        displayLabel: "SELECT HEIGHT",
        displayLabelByValue: { ROCKS: "SELECT SIZE" },
        options: opts(faciltyHazardAreaSize, [
          "SMALL",
          "MEDIUM",
          "LARGE",
          "SHORT",
          "TALL",
        ]),
        optionsByValue: {
          "DESERT | LEAF FOLIAGE": ["SHORT", "MEDIUM", "TALL"],
          "DESERT | SHRUB FOLIAGE": ["SHORT", "MEDIUM", "TALL"],
          "DESERT | LEAF AND SHRUB FOLIAGE": ["SHORT", "MEDIUM", "TALL"],
          "LANDSCAPE SOIL | LEAF FOLIAGE": ["SHORT", "MEDIUM", "TALL"],
          "LANDSCAPE SOIL | SHRUB FOLIAGE": ["SHORT", "MEDIUM", "TALL"],
          "LANDSCAPE SOIL | LEAF AND SHRUB FOLIAGE": ["SHORT", "MEDIUM", "TALL"],
          ROCKS: ["SMALL", "MEDIUM", "LARGE"],
          WETLAND: [],
          "WOOD CHIPS": [],
          "SHORT GRASS": [],
          DESERT: [],
          "LANDSCAPE SOIL": [],
          "BUNKER GRASS": ["SHORT", "MEDIUM", "TALL"],
          REEDS: ["SHORT", "MEDIUM", "TALL"],
          SWAMPGRASS: ["SHORT", "MEDIUM", "TALL"],
          "MEADOW GRASS": ["SHORT", "MEDIUM", "TALL"],
          "LINKS GRASS": ["SHORT", "MEDIUM", "TALL"],
          "PINE STRAW": [],
          CACTUS: ["SHORT", "MEDIUM", "TALL"],
        },
        fallback: [],
      },
    ],
  },

  Fixtures: {
    title: "Fixtures",
    fields: [
      {
        slot: "type",
        label: "SELECT TYPE",
        required: true,
        options: opts(faciltyFixtureType, [
          "FOUNTAIN",
          "BATHROOM",
          "FOOD SERVICE",
          "SHED",
        ]),
      },
      {
        slot: "size",
        label: "SELECT SIZE",
        required: true,
        options: opts(faciltyHazardAreaSize, ["SMALL", "MEDIUM", "LARGE"]),
      },
    ],
  },

  Forest: {
    title: "Forest",
    fields: [
      {
        slot: "type",
        label: "SELECT TYPE",
        required: true,
        options: opts(faciltyForestType, [
          "TROPICAL FOREST",
          "DECIDUOUS FOREST",
          "CONIFEROUS FOREST",
        ]),
      },
    ],
  },

  Bunker: { title: "Bunker", fields: bunkerFamilyFields },

  // Out-of-spec but constants-driven: kept under the engine for consistency.
  // Legacy validation required all three — preserved to avoid UX regression
  // for these elements that the new spec doesn't cover.
  Clubhouse: {
    title: "Clubhouse",
    fields: [
      {
        slot: "description",
        label: "SELECT DESCRIPTION",
        required: true,
        options: opts(clubhouseDesc, [
          "CAPE COD",
          "COLONIAL",
          "MEDITERRANEAN",
          "RUSTIC",
          "MODERN",
        ]),
      },
      {
        slot: "type",
        label: "SELECT TYPE",
        displayLabel: "SELECT COLOR",
        required: true,
        options: opts(clubhouseType, [
          "WHITE",
          "LIGHT BLUE",
          "DARK BLUE",
          "GRAY",
          "TAN",
          "LIGHT YELLOW",
          "GREEN",
          "CREAM",
          "BROWN",
          "BRICK",
          "GLASS",
        ]),
      },
      {
        slot: "size",
        label: "SELECT SIZE",
        required: true,
        options: opts(clubhouseSize, ["SINGLE STORY", "MULTI STORY"]),
      },
    ],
  },

  Creek: {
    title: "Creek",
    fields: [
      {
        slot: "size",
        label: "SELECT SIZE",
        required: true,
        options: opts(creekSize, ["NARROW", "MEDIUM", "WIDE"]),
      },
    ],
  },
};

// Title (popover headline) -> element key. Word boundaries protect against
// prefix collisions: tree/street, water/waterfall, path/centralpath,
// clubhouse/clubhousecenter, bunker/practicebunker, native/nativearea.
//
// Order matters: longer/more-specific titles are matched first; the early
// returns of null short-circuit the *center / *fall variants before they
// would otherwise match their shorter cousins below.
export const resolveElementKey = (title) => {
  if (!title) return null;
  const t = title.toLowerCase();

  if (t.includes("practicebunker") || t.includes("practice bunker")) return "PracticeBunker";
  if (t.includes("clubhousecenter") || t.includes("club center")) return null;
  if (t.includes("centralpath")) return null;
  if (t.includes("waterfall")) return null;

  if (t.includes("native")) return "NativeArea";
  if (t.includes("background")) return "Background";
  if (t.includes("residence")) return "Residence";
  if (t.includes("condo")) return "Condo";
  if (t.includes("hotel")) return "Hotel";
  if (t.includes("business")) return "Business";
  if (t.includes("parking")) return "ParkingLot";
  if (t.includes("fence")) return "Fence";
  if (t.includes("fixtures")) return "Fixtures";
  if (t.includes("forest")) return "Forest";
  if (t.includes("vegetation")) return "Vegetation";
  if (t.includes("bridge")) return "Bridge";
  if (t.includes("cliff")) return "Cliff";
  if (t.includes("lava")) return "Lava";
  if (t.includes("creek")) return "Creek";
  if (t.includes("sand")) return "Sand";
  if (t.includes("rock")) return "Rock";
  if (t.includes("water")) return "Water";
  if (t.includes("street")) return "Street";
  if (t.includes("tree")) return "Tree";
  if (t.includes("clubhouse")) return "Clubhouse";
  if (t.includes("path")) return "CartPath";
  if (t.includes("bunker")) return "Bunker";

  return null;
};
